<template>
  <jw-trigger trigger="click" unmount-on-close position="bl" :popup-visible="data.popupVisible" :show-arrow="true" :popup-offset="4" arrow-class="arrow-filter" popup-container=".comp-ly-advanced-filter">
    <jw-button class="list-container-btn" :type="groupShowValue?.length === 0 ? 'secondary' : 'outline'" :class="groupShowValue?.length > 0 ? 'filter-btn' : 'default-btn'" size="small" @click="handleFilterClick">
      <template #icon>
        <qfei-icon-grouping-outlined />
      </template>
      <jw-space size="mini">
        <span v-if="groupShowValue?.length">{{ groupShowValue?.length }}</span>
        <span>分组</span>
      </jw-space>
    </jw-button>
    <template #content>
      <ly-skeleton v-if="loading" fromType="list-group"></ly-skeleton>
      <div class="content-basic" v-else>
        <div class="group-condition">
          <jw-space>
            <span class="group-condition-title">设置分组条件</span>
            <jw-tooltip>
              <template #content>
                <ul class="prompt-text">
                  <li>最多支持三个分组条件</li>
                  <li>字段类型排序规则：</li>
                  <ul class="more-text">
                    <li>日期：按照时间的正序、倒序</li>
                    <li>数字：按照数字的正序、倒序</li>
                    <li>金额：按照金额的正序、倒序</li>
                    <li>其余可分组字段类型不支持排序</li>
                  </ul>
                </ul>
              </template>
              <qfei-icon-question-mark-outlined class="hand" />
            </jw-tooltip>
          </jw-space>
          <jw-button size="small" type="text" v-if="data.groupList.length" @click="clearCondition" class="clear-condition">清空所有</jw-button>
        </div>
        <div class="group-list">
          <draggable itemKey="index" :list="data.groupList" handle=".mover">
            <template #item="{ element, index }">
              <div class="draggable-item">
                <qfei-icon-more-two-outlined class="mover icon-mover" />
                <div class="task-is-required">
                  <template v-if="element.fieldStatus == 2">
                    <div class="text-value">{{ getStoreFieldName(element) }}</div>
                  </template>
                  <template v-if="element.fieldStatus == 1">
                    <jw-select class="select-group-field" :default-popup-visible="visibleMap[`show-${index}`]" v-model="element.fieldKey" allow-search placeholder="请选择分组字段" @change="fieldChange(data.groupList, data.groupFields, 10)" @popup-visible-change="(event) => selectChangePopup(event, element)" :fallback-option="fallback">
                      <template #label="{ data }">
                        <div class="select-group-field-label">{{ data?.label }}</div>
                      </template>
                      <template v-for="(item, index) in data.groupFields" :key="index">
                        <fh-divider v-if="item?.isFirstTypeOne"></fh-divider>
                        <jw-option :value="item.fieldKey" :label="item.fieldName" :disabled="item.disabled">
                          {{ item.fieldName }}
                        </jw-option>
                      </template>
                    </jw-select>
                  </template>
                </div>
                <div class="field-sort">
                  <jw-tooltip :content="'升序'">
                    <jw-button type="text" size="small" :disabled="sortDisabled(element)" class="icon-sort-btn" :class="[element.fieldSort ? 'field-sort-selected' : 'field-sort-up']" @click="setGroupSort(true, index)">
                      <qfei-icon-ascending-order-outlined />
                    </jw-button>
                  </jw-tooltip>
                  <jw-tooltip :content="'降序'">
                    <jw-button type="text" size="small" :disabled="sortDisabled(element)" class="icon-sort-btn" :class="[element.fieldSort ? 'field-sort-up' : 'field-sort-selected']" @click="setGroupSort(false, index)">
                      <qfei-icon-descending-order-outlined />
                    </jw-button>
                  </jw-tooltip>
                  <jw-tooltip :content="'删除'">
                    <jw-button type="text" size="small" class="icon-sort-btn delete-icon" @click="deleteSortField(index)">
                      <qfei-icon-delete-outlined />
                    </jw-button>
                  </jw-tooltip>
                </div>
              </div>
            </template>
          </draggable>
        </div>
        <div style="display: flex; align-items: center">
          <div class="group-add-condition">
            <jw-button @click="addGroupCondition" v-show="data.groupList?.length < 3" type="text" size="small" class="add-group-condition">
              <template #icon>
                <qfei-icon-add-two-outlined />
              </template>
              添加分组条件
            </jw-button>
          </div>
          <jw-button @click="saveOrUpdate" type="primary" size="small">确认</jw-button>
        </div>
      </div>
    </template>
  </jw-trigger>
  <div v-if="showOverlay" class="overlay" @click.stop="hideLay"></div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue';
import draggable from 'vuedraggable';
import { layoutService } from '@/api/';
import { Message } from '@arco-design/web-vue';
import useLoading from '@/hooks/loading';
import { QfeiIconGroupingOutlined, QfeiIconAscendingOrderOutlined, QfeiIconAddTwoOutlined, QfeiIconDeleteOutlined, QfeiIconDescendingOrderOutlined, QfeiIconMoreTwoOutlined, QfeiIconQuestionMarkOutlined } from '@qfei-design/icons-vue';
import useSortGroup from '@/hooks/sort-group';
const { handleOptionsDisabled, fieldChange } = useSortGroup();
const { loading, setLoading } = useLoading(false);
import useFields from '@/hooks/fields';
const { moveTypeOneToEnd } = useFields();
import fhDivider from '@/components/fh-divider/fh-divider.vue';
const props = defineProps({
  viewId: {
    type: [String, Number],
    default: ''
  },
  entity: {
    type: [String, Number],
    default: 1
  },
  fieldGroups: {
    type: Array,
    default: () => []
  }
});
const showOverlay = ref(false);
const data = reactive({
  popupVisible: false,
  groupList: [],
  groupListValue: [],
  groupFields: [],
  num: 0,
  // 初始分组数据
  copy1: '',
  // 待提交数据
  copy2: ''
});

const fallback = (fieldKey) => {
  const fileldName = data.groupList?.find((item) => item.fieldKey == fieldKey)?.fieldName;
  return { value: fieldKey, label: fileldName, disabled: true };
};

const getStoreFieldName = (element) => {
  return element.fieldName + ' (已停用)';
};

const fieldGroups = computed(() => props.fieldGroups);
const visibleMap = ref({}); // 字段列表是否显示
const emits = defineEmits('cbGroup');
// 设置排序
const setGroupSort = (type, index) => {
  data.groupList?.forEach((item, i) => {
    if (i == index) {
      item.fieldSort = type;
    }
  });
};
const sortDisabled = (element) => {
  const fieldType = data.groupFields?.find((item) => item.fieldKey == element.fieldKey)?.fieldType;
  return ![2, 4, 7, 8, 16]?.includes(fieldType) && element.fieldStatus != 2;
};

const addGroupCondition = () => {
  if (data.groupList?.length < 3) {
    data.groupList.push({ fieldKey: '', fieldSort: true, fieldStatus: 1, subProperty: '' });
    const len = data.groupList?.length - 1;
    visibleMap.value[`show-${len}`] = true;
    handleOptionsDisabled(data.groupList, data.groupFields);
  }
};
// 打开分组弹窗
const handleFilterClick = async () => {
  setLoading(true);
  data.groupList = JSON.parse(JSON.stringify(fieldGroups.value));
  // 日期范围特殊处理
  data.fieldSort =
    data.groupList?.map((item) => {
      if (item?.subProperty && item?.subProperty == 'level1') {
        item.fieldKey = item.fieldKey + '-level1';
      }
      if (item?.subProperty && item?.subProperty == 'level2') {
        item.fieldKey = item.fieldKey + '-level2';
      }
      if (item?.subProperty && item?.subProperty == 'level3') {
        item.fieldKey = item.fieldKey + '-level3';
      }
      return item;
    }) || [];
  // 如果分组列表为空，则添加一个默认分组条件
  if (data.groupList?.length == 0) {
    data.groupList.push({ fieldKey: '', fieldSort: true, fieldStatus: 1, subProperty: '' });
    visibleMap.value[`show-0`] = true;
  }
  data.popupVisible = true;
  showOverlay.value = true;
  await init();
  setLoading(false);
};
const hideLay = async () => {
  // nextTick(() => {
  console.log(123456, 123, visibleMap.value);
  showOverlay.value = false;
  data.popupVisible = false;
  visibleMap.value = {};
  // });
};
const clearCondition = async () => {
  data.groupList = [];
  saveOrUpdate();
};

const deleteSortField = (index) => {
  if (data.groupList.length == 1) {
    data.groupList = [];
  } else {
    data.groupList?.splice(index, 1);
  }
  handleOptionsDisabled(data.groupList, data.groupFields);
};

// 禁用状态特殊处理
const selectChangePopup = (event, element) => {
  if (!element?.fieldKey) return;
  const keys = element.fieldKey?.split('-');
  data.groupFields = data.groupFields?.map((e) => {
    if (e.copyFieldKey == keys[0] && element.fieldKey) {
      e.disabled = !event;
    }
    return { ...e };
  });
};

const addItemCascade = (data) => {
  const result = [];
  for (const item of data) {
    if (item.fieldType === 10 && item.maxLevel <= 3) {
      // 这是级联选择字段，需要拆分成多个层级
      for (let level = 1; level <= item.maxLevel; level++) {
        result.push({
          ...item,
          id: item.id, // 保持相同ID
          copyFieldKey: item.fieldKey, // 复制一份fieldKey
          fieldName: `${item.fieldName}/${getLevelName(level)}`,
          fieldKey: `${item.fieldKey}-level${level}` // 保持相同fieldKey
        });
      }
    } else {
      // 其他字段直接保留
      result.push({ ...item, copyFieldKey: item.fieldKey });
    }
  }
  return result;
};

const getLevelName = (level) => {
  const levelNames = ['第一层级', '第二层级', '第三层级'];
  return levelNames[level - 1] || `第${level}层级`;
};

const getEntityFields = async () => {
  try {
    if (!props.viewId) {
      return;
    }
    const json = await layoutService.groupField({ entity: props.entity, viewId: props.viewId });
    if (!json.error) {
      const fields = json.result || [];
      if (fields && fields.length) {
        data.groupFields = fields;
        data.groupFields = data.groupFields?.filter((item) => item.fieldIsGroup === true);
        // 系统字段-自定义字段排序
        data.groupFields = moveTypeOneToEnd(data.groupFields);
        // 级联特殊处理
        if (data.groupFields?.length) {
          data.groupFields = addItemCascade(data.groupFields);
        }
      }
    } else {
      Message.error(json.error?.msg || '失败，请重试!');
    }
  } catch (e) {
    Message.error(e.message || e.statusText || '失败，请重试!');
  }
};

// 保存分组
const saveOrUpdate = async () => {
  try {
    showOverlay.value = false;
    data.popupVisible = false;
    // 级联特殊处理
    data.groupList?.forEach((item) => {
      if (item.fieldKey?.includes('-')) {
        const _str = item.fieldKey?.split('-');
        if (_str[0]) {
          item.fieldKey = _str[0];
        }
        if (_str[1]) {
          item.subProperty = _str[1];
        }
      }
    });

    data.copy2 = JSON.stringify(data.groupList);
    console.log('data.copy1', data.copy1);
    console.log('data.copy2', data.copy2);
    visibleMap.value = {};
    // 判断是否保存
    if (data.copy1 === data.copy2) {
      // 无改变
      return;
    }

    const fieldGroups = data.groupList?.filter((item) => item.fieldKey);
    groupShowValue.value = data.groupList?.filter((item) => item.fieldKey != '') || [];

    const params = {
      entity: props.entity,
      viewId: props.viewId
    };
    if (fieldGroups?.length) {
      params.fieldGroups = fieldGroups.map((item) => {
        return { ...item };
      });
    } else {
      params.fieldGroups = [];
    }
    const json = await layoutService.saveOrUpdate(params);
    if (!json.error) {
      // data?.openListGroup(fieldGroups);
      visibleMap.value = {};
      emits('cbGroup', fieldGroups);
      data.copy1 = data.copy2;
    } else {
      Message.error(json.error?.msg || '失败，请重试!');
    }
  } catch (e) {
    Message.error(e.message || e.statusText || '失败，请重试!');
  }
};

const init = async () => {
  // 获取可以分组的字段
  await getEntityFields();
};
const groupShowValue = ref([]);

onMounted(() => {
  if (fieldGroups.value?.length) {
    data.groupList = JSON.parse(JSON.stringify(fieldGroups.value));
    groupShowValue.value = data.groupList?.filter((item) => item.fieldKey != '') || [];
  }
  // 保存最初值
  data.copy1 = JSON.stringify(data.groupList);
});
</script>
<style scoped lang="less">
@import './list-group.less';
</style>
